import { Component, type ErrorInfo, type ReactNode } from "react";
import { flushActivity, trackError } from "../../telemetry/activity";
import { errorMessage } from "../../telemetry/errorMessage";
import { useRouter } from "../../store/router";

/**
 * 스테이지 렌더 크래시 복구 (BOOTH-97) — 흰 화면으로 멈추지 않고 손님을 첫 화면으로 돌려보낸다.
 *
 * 크래시를 `client_error(render_crash)` 로 남기고 즉시 flush 한 뒤, 부모(`AppShell`)가 준 `onCrash` 의
 * 판정(`crashPolicy`)에 따라 1.5초 뒤 세션 리셋 또는 페이지 새로고침을 한다.
 * 폴백 문구는 운영 오류 화면이라 다국어 대상이 아니다 — 문구 카탈로그 없이 한국어 하드코딩.
 */
export class StageErrorBoundary extends Component<
  { onCrash: () => "reset" | "reload"; children: ReactNode },
  { crashed: boolean }
> {
  state = { crashed: false };
  private resetTimer: number | null = null;

  static getDerivedStateFromError(): { crashed: boolean } {
    return { crashed: true };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    // 스택 첫 프레임만 — 어느 컴포넌트에서 깨졌는지 알면 충분하고, 전체 스택은 200자 한도를 넘긴다.
    // 오류 문구를 140자로 줄여 뒤의 `@ <컴포넌트>` 프레임이 잘려 나가지 않게 한다(최종 절단은 trackError).
    const where = info.componentStack?.split("\n")[1]?.trim() ?? "";
    // 상세엔 컴포넌트 스택 전체(≤2000자) 를 싣는다 (BOOTH-109) — 행은 여전히 한 줄.
    trackError("render_crash", err, {
      message: `${errorMessage(err, 140)} @ ${where}`,
      ...(info.componentStack ? { stackText: `${err.stack ?? err.message}\n--- component stack ---${info.componentStack}` } : {}),
    });
    void flushActivity();
    if (this.props.onCrash() === "reload") {
      window.location.reload();
      return;
    }
    this.resetTimer = window.setTimeout(() => {
      this.resetTimer = null;
      useRouter.getState().resetSession();
      this.setState({ crashed: false });
    }, 1_500);
  }

  componentWillUnmount(): void {
    if (this.resetTimer !== null) window.clearTimeout(this.resetTimer);
  }

  render(): ReactNode {
    if (this.state.crashed) {
      return (
        <div className="view">
          <p>잠시 후 처음 화면으로 돌아갑니다</p>
        </div>
      );
    }
    return this.props.children;
  }
}
